import type { AnalysisSubject } from 'venn-utils';
import { type SubjectInputId, type SubjectWithOptionalFeeAndFilters, type WorkspaceFeeLimits } from 'venn-state';
import type { FeeAdjustType, SubjectFee } from './types';

/**
 * Applies a fee adjustment to each subject in every input group, keyed by the subject's own ID.
 * When `adjustFeeFn` returns a value different from the current fee, `feesMapping` is updated
 * (or created if absent).
 */
export const adjustSubjectGroupFees = (
  subjectGroups: Record<SubjectInputId, SubjectWithOptionalFeeAndFilters[]>,
  adjustFeeFn: (params: { subjectId: string; fee: number | undefined }) => number | undefined,
): Record<SubjectInputId, SubjectWithOptionalFeeAndFilters[]> => {
  return Object.fromEntries(
    Object.entries(subjectGroups).map(([groupId, subjects]) => [
      groupId,
      subjects.map((subject) => {
        const publicId =
          subject.type === 'portfolio' ? subject.portfolioId : subject.type === 'fund' ? subject.fundId : undefined;
        if (publicId === undefined) {
          return subject;
        }

        const subjectId = String(publicId);

        const subjectFee = subject.feesMapping?.[subjectId];
        const adjustedFee = adjustFeeFn({ subjectId, fee: subjectFee });

        if (subjectFee === adjustedFee) {
          return subject;
        }

        return {
          ...subject,
          feesMapping: {
            ...(subject.feesMapping ?? {}),
            [subjectId]: adjustedFee ?? 0,
          },
        };
      }),
    ]),
  );
};

/**
 * Joins raw studio subjects with their resolved `AnalysisSubject` counterparts
 * and returns only the entries that have a non-empty subject id.
 */
export const mapSubjectsToFees = (
  rawSubjects: SubjectWithOptionalFeeAndFilters[],
  subjects: AnalysisSubject[],
): SubjectFee[] => {
  return rawSubjects
    .map((rawSubject) => {
      const portfolioId = rawSubject.type === 'portfolio' ? rawSubject.portfolioId : undefined;
      const fundId = rawSubject.type === 'fund' ? rawSubject.fundId : undefined;
      return {
        portfolio:
          portfolioId !== undefined ? subjects.find(({ portfolio }) => portfolio?.id === portfolioId) : undefined,
        fund: fundId ? subjects.find(({ fund }) => fund?.id === fundId) : undefined,
        fee: rawSubject.feesMapping?.[portfolioId ?? fundId!],
      };
    })
    .map(({ fund, portfolio, fee }) => ({
      subjectId: fund?.id ?? portfolio?.id,
      subjectName: fund?.name ?? portfolio?.name ?? '',
      fee: fee ?? 0,
    }))
    .filter((item): item is SubjectFee => item.subjectId !== undefined);
};

/**
 * Converts workspace fee limits from the API's percent scale (0–100) to the
 * decimal scale (0–1) used by subject `feesMapping` values.
 */
export const normalizeFeeLimits = (limits: WorkspaceFeeLimits): WorkspaceFeeLimits => ({
  minFee: limits.minFee / 100,
  maxFee: limits.maxFee / 100,
  defaultFee: limits.defaultFee / 100,
});

/**
 * Returns only the fees that fall outside the workspace-defined min/max range.
 */
export const filterFeesOutsideLimits = (fees: SubjectFee[], limits: WorkspaceFeeLimits): SubjectFee[] =>
  fees.filter(({ fee }) => fee < limits.minFee || fee > limits.maxFee);

/**
 * Adjusts a single fee to fit within [minFee, maxFee].
 * - `'default'` snaps to `defaultFee`.
 * - `'minmax'` clamps to the nearest boundary.
 * Returns the fee unchanged if it is already within range.
 */
export const adjustFee = ({
  fee,
  adjustType,
  minFee,
  maxFee,
  defaultFee,
}: {
  fee: number;
  adjustType: FeeAdjustType;
  minFee: number;
  maxFee: number;
  defaultFee: number;
}): number => {
  if (fee >= minFee && fee <= maxFee) {
    return fee;
  }

  if (adjustType === 'default') {
    return defaultFee;
  }

  return Math.min(Math.max(minFee, fee), maxFee);
};
